import { type ActionEvent, Controller } from "@hotwired/stimulus"

import DrawerController from "../drawer_controller"

import BookingFooterController from "../venue/booking_footer_controller"
import RoomCounterController from "../venue/room_counter_controller"

import { type DispatchEvent } from "@/entry_points/types"

import { formatEEEDMMMYYYY } from "@/helpers/date_format"
import * as Ga4 from "@/helpers/ga4_helper"
import { loadBackgroundImages } from "@/helpers/image_loader"
import { pluralize } from "@/helpers/pluralize"
import { ENTRY, prefetchEntry } from "@/helpers/prefetch"
import { setInputValueWithEvents } from "@/helpers/utils"

export default class extends Controller {
  static targets = [
    "calendarDrawerSummary",
    "calendarEditButton",
    "calendarStream",
    "dateRadio",
    "facilitiesDrawerSummary",
    "facilitiesStream",
    "facilityRadio",
    "formSubmitButton",
    "hiddenDateInput",
    "hiddenFacilityInput",
    "hiddenRoomsInputsContainer",
    "maxPaxNotice",
    "modalHeader",
    "paxInput",
    "paxSummary",
    "roomsDrawerSummary",
    "roomsStream",
    "unavailableDateNotice"
  ]
  declare readonly calendarDrawerSummaryTarget: HTMLElement
  declare readonly calendarEditButtonTarget: HTMLElement
  declare readonly dateRadioTargets: HTMLInputElement[]
  declare readonly facilitiesDrawerSummaryTarget: HTMLElement
  declare readonly facilitiesStreamTarget: HTMLElement
  declare readonly facilityRadioTargets: HTMLInputElement[]
  declare readonly formSubmitButtonTarget: HTMLButtonElement
  declare readonly hiddenDateInputTarget: HTMLInputElement
  declare readonly hiddenFacilityInputTargets: HTMLInputElement[]
  declare readonly hiddenRoomsInputsContainerTargets: HTMLDivElement[]
  declare readonly maxPaxNoticeTarget: HTMLElement
  declare readonly modalHeaderTarget: HTMLElement
  declare readonly paxInputTarget: HTMLInputElement
  declare readonly paxSummaryTarget: HTMLElement
  declare readonly roomsDrawerSummaryTarget: HTMLElement
  declare readonly roomsStreamTarget: HTMLElement
  declare readonly unavailableDateNoticeTarget: HTMLElement

  declare readonly hasCalendarStreamTarget: boolean
  declare readonly hasRoomsStreamTarget: boolean

  static outlets = ["booking-footer", "drawer", "room-counter"]
  declare readonly bookingFooterOutlet: BookingFooterController
  declare readonly drawerOutlets: DrawerController[]
  declare readonly roomCounterOutlets: RoomCounterController[]

  static values = { ecommerceItem: Object, nights: Number }
  declare readonly ecommerceItemValue: Ga4.EcommerceItem
  declare readonly nightsValue: number

  calendarStreamTargetConnected(): void {
    const isDateSelected = !!this.selectedDateRadio

    prefetchEntry(ENTRY.BOOKINGS)

    // Hide notice when date is selected. We don't show it in the opposite case,
    // otherwise notice would show on load whenever there's no date in params, which
    // we don't want (if no date was selected, it can't be "unavailable").
    if (isDateSelected) {
      this.unavailableDateNoticeTarget.hidden = true
    }

    this.setCalendarSubmit(isDateSelected)
  }

  facilitiesStreamTargetConnected(): void {
    this.bookingFooterOutlet.setFacilitiesSubmit(!!this.selectedFacilityRadio)
  }

  roomsStreamTargetConnected(element: HTMLElement): void {
    // Load fallback image for rooms with no carousel
    loadBackgroundImages(element.querySelectorAll("[data-img]"))

    const remainingPax = this.selectedPax - this.paxForSelectedRooms

    this.roomCounterOutlets.forEach((counter) => {
      counter.setMax(remainingPax)
    })
    this.bookingFooterOutlet.setRoomsSubmit(remainingPax === 0)
  }

  addToCart(): void {
    Ga4.addToCartEvent(
      {
        ...this.ecommerceItemValue,
        item_category3: this.selectedPax.toString(),
        price: this.priceForSelectedRooms,
        quantity: 1
      },
      this.priceForSelectedRooms
    )
  }

  confirmPax({ params: { price } }: ActionEvent): void {
    this.paxSummaryTarget.textContent = pluralize(this.selectedPax, "guest")

    // Confirming pax goes to calendar step and price summary updated there, except:
    // 1. If page loaded with date param there's no calendar and thus no "selected" date. In that
    //    case we get the price from server as a param to update price summary.
    if (price) {
      this.bookingFooterOutlet.setPriceTarget(+price * this.selectedPax)
    }

    // 2. If date is already selected (e.g. going back to pax step from another step). In that
    //    case re-select saved date to trigger #dateSelected (which fires on `change`, not `click`)
    //    to update price summary for latest pax value.
    const { selectedDateRadio } = this

    if (selectedDateRadio) {
      setInputValueWithEvents(selectedDateRadio, selectedDateRadio.value, ["change"])
    }
  }

  dateSelected({ params: { price }, target }: ActionEvent): void {
    this.unavailableDateNoticeTarget.hidden = true

    if (price && (target as HTMLInputElement).checked) {
      this.bookingFooterOutlet.setPriceTarget(+price * this.selectedPax)
      this.setCalendarSubmit(true)
    } else {
      this.setCalendarSubmit(false)
    }
  }

  facilitySelected(): void {
    this.bookingFooterOutlet.setFacilitiesSubmit(true)
    this.hiddenFacilityInputTargets.forEach(
      (input) => (input.value = this.selectedFacilityRadio!.value)
    )
  }

  onCalendarDrawerClick(e: ActionEvent): void {
    const paxDrawer = this.drawerOutlets.find((drawer) =>
      drawer.element.contains(this.paxSummaryTarget)
    )!

    // Calendar drawer should fetch fresh calendar in two cases:
    // 1. Arriving at calendar from pax step (i.e. pax drawer is open)
    // 2. Calendar has never been loaded yet (e.g. date already in URL params)
    if (paxDrawer.openValue || !this.hasCalendarStreamTarget) {
      e.stopPropagation()
      this.calendarEditButtonTarget.click()
    } else {
      this.calendarStreamTargetConnected()
    }
  }

  onPaxDrawerOpen(): void {
    this.bookingFooterOutlet.setPaxSubmit()
  }

  roomSelected({ detail }: DispatchEvent<{ delta: number; name: string }>): void {
    const remainingPax = this.selectedPax - this.paxForSelectedRooms
    const { delta, name } = detail

    this.roomCounterOutlets.forEach((counter) => {
      // Counters with the same `name` represent one room, with different occupancies. The available
      // units for a room are for all occupancies, so when one changes we increase/decrease
      // availability of all of them. This value is used in max calculation, so must be set before
      // max below.
      if (counter.name === name) {
        counter.setAvailable(delta)
      }

      counter.setMax(remainingPax)
    })

    this.bookingFooterOutlet.setPriceTarget(this.priceForSelectedRooms)
    this.bookingFooterOutlet.setRoomsSubmit(remainingPax === 0)
    this.updateSelectedRoomsInputs()
  }

  saveDate(): void {
    if (this.selectedDateRadio) {
      const isoDate = this.selectedDateRadio.value
      const formattedDate = formatEEEDMMMYYYY(new Date(isoDate))
      const formattedNights =
        this.nightsValue > 0 ? `(${pluralize(this.nightsValue, "night")})` : ""

      this.updateSummary(this.calendarDrawerSummaryTarget, `${formattedDate} ${formattedNights}`)

      // Update the hidden date target in the pax drawer, so it will overwrite any date from params.
      // That input is necessary to enable skipping the calendar drawer if date already present.
      this.hiddenDateInputTarget.value = isoDate
    } else {
      this.updateSummary(this.calendarDrawerSummaryTarget, "No date selected", false)
      this.hiddenDateInputTarget.value = ""
    }
  }

  saveFacility(): void {
    if (this.selectedFacilityRadio) {
      const selectedFacility = this.selectedFacilityRadio.parentElement!.querySelector(
        ".js-facility-description"
      )!.textContent

      this.updateSummary(this.facilitiesDrawerSummaryTarget, selectedFacility)
    }
  }

  saveRooms(): void {
    if (this.paxForSelectedRooms > 0) {
      const selectedRooms = this.selectedRooms
      const summaryText = [...selectedRooms]
        .reduce<string[]>((summary, [counter, counts]) => {
          summary.push(`${counts.length} x ${counter.roomNameValue}`)
          return summary
        }, [])
        .join(", ")

      this.updateSummary(this.roomsDrawerSummaryTarget, summaryText)
    } else {
      this.resetRoomsSelection()
    }
  }

  scrollDrawerIntoView({ currentTarget }: ActionEvent): void {
    const drawer = this.drawerOutlets.find((drawer) =>
      drawer.element.contains(currentTarget as HTMLElement)
    )!

    // Date drawer is a special case -- we want to scroll to the selected date, not top of drawer.
    const { selectedDateRadio } = this
    const elementToScroll =
      selectedDateRadio && drawer.element.contains(selectedDateRadio)
        ? selectedDateRadio
        : drawer.element

    drawer.afterTransition(() => {
      // Set scroll margin offset to modal header height, plus padding.
      const PADDING = 16
      drawer.element.style.setProperty(
        "--drawer-top-offset",
        `${this.modalHeaderTarget.offsetHeight + PADDING}px`
      )

      elementToScroll.scrollIntoView({ behavior: "smooth" })
    })
  }

  submitForm(): void {
    this.formSubmitButtonTarget.click()
  }

  toggleMaxPaxNotice({ detail }: DispatchEvent<{ count: number; max: number }>): void {
    const { count, max } = detail
    this.maxPaxNoticeTarget.hidden = count < max
  }

  resetRoomsSelection(): void {
    if (this.hasRoomsStreamTarget) {
      this.hiddenRoomsInputsContainerTargets.forEach((container) => (container.innerHTML = ""))
      this.updateSummary(this.roomsDrawerSummaryTarget, "No rooms selected", false)
    }
  }

  private updateSummary(target: HTMLElement, summaryText: string, show: boolean = true): void {
    const summaryTextElement = target.querySelector(".js-summary-text") as HTMLElement
    const checkIcon = target.querySelector(".js-summary-check-icon") as HTMLElement
    const editButton = target.querySelector(".js-summary-edit-button") as HTMLButtonElement

    summaryTextElement.textContent = summaryText
    checkIcon.hidden = !show
    editButton.hidden = !show
  }

  private get selectedDateRadio(): HTMLInputElement | undefined {
    return this.dateRadioTargets.find((radio) => radio.checked)
  }

  private get selectedFacilityRadio(): HTMLInputElement | undefined {
    return this.facilityRadioTargets.find((radio) => radio.checked)
  }

  private get selectedRooms(): Map<RoomCounterController, number[]> {
    return this.roomCounterOutlets.reduce((rooms, roomCounter) => {
      const { countValue, occupancyValue } = roomCounter

      if (countValue > 0) {
        const occupancies = rooms.get(roomCounter) ?? []

        for (let i = 0; i < countValue; i++) {
          occupancies.push(occupancyValue)
        }

        rooms.set(roomCounter, occupancies)
      }

      return rooms
    }, new Map<RoomCounterController, number[]>())
  }

  private get selectedPax(): number {
    return +this.paxInputTarget.value
  }

  private setCalendarSubmit(isDateSelected: boolean): void {
    this.bookingFooterOutlet.setCalendarSubmit({
      isDateSelected,
      isSpaDay: this.nightsValue === 0
    })
  }

  private get paxForSelectedRooms(): number {
    return this.roomCounterOutlets.reduce((sum, counter) => sum + counter.selectedPax, 0)
  }

  private get priceForSelectedRooms(): number {
    return this.roomCounterOutlets.reduce((sum, counter) => sum + counter.selectedPrice, 0)
  }

  private updateSelectedRoomsInputs(): void {
    this.hiddenRoomsInputsContainerTargets.forEach((container) => (container.innerHTML = ""))

    const selectedRooms = this.selectedRooms
    selectedRooms.forEach((occupancies, roomCounter) => {
      occupancies.forEach((occupancy) => {
        this.hiddenRoomsInputsContainerTargets.forEach((container) => {
          const input = document.createElement("input")
          input.type = "hidden"
          input.name = `selected_rooms[${roomCounter.roomIdValue}][]`
          input.value = occupancy.toString()
          container.appendChild(input)
        })
      })
    })
  }
}
